import React from "react";
import "./App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { CartProvider } from "./context/CartContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import CookieConsent from "./components/CookieConsent";
import SessionInactivityProvider from "./components/SessionInactivityProvider";

// Global axios config: send httpOnly cookies with every request
axios.defaults.withCredentials = true;

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import ChatWidget from "./components/ChatWidget";
import AdminLayout from "./components/AdminLayout";

// Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Ecosystem from "./pages/Ecosystem";
import Gallery from "./pages/Gallery";
import Contact from "./pages/Contact";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import MyOrders from "./pages/MyOrders";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Auth from "./pages/Auth";
import Investment from "./pages/Investment";
import Dashboard from "./pages/Dashboard";
import InvestorDashboard from "./pages/InvestorDashboard";
import CustomerDashboard from "./pages/CustomerDashboard";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/auth/VerifyEmail";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminAds from "./pages/AdminAds";
import AdminProducts from "./pages/AdminProducts";
import AdminEcosystem from "./pages/AdminEcosystem";
import AdminUsers from "./pages/AdminUsers";
import AdminBlog from "./pages/AdminBlog";
import AdminOrders from "./pages/AdminOrders";
import AdminSettings from "./pages/AdminSettings";
import AdminBankAccounts from "./pages/AdminBankAccounts";
import AdminChat from "./pages/AdminChat";
import AdminInvestments from "./pages/AdminInvestments";
import AdminWholesale from "./pages/AdminWholesale";
import AdminEmailTemplates from "./pages/AdminEmailTemplates";
import AdminNotifications from "./pages/AdminNotifications";
import AdminFarmers from "./pages/AdminFarmers";
import AdminCooperativeLoanInterests from "./pages/AdminCooperativeLoanInterests";
import TrustedDevices from "./pages/TrustedDevices";
import FarmerApply from "./pages/FarmerApply";
import FarmerListings from "./pages/FarmerListings";
import FarmersMarket from "./pages/FarmersMarket";
import Wholesale from "./pages/Wholesale";
import TrackOrder from "./pages/TrackOrder";
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import Cookies from "./pages/legal/Cookies";

// Layout wrapper to conditionally show header/footer
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // CRITICAL: Detect session_id in URL hash for Google OAuth callback
  // Must be synchronous during render, not in useEffect (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-green-700 focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg"
        data-testid="skip-to-content"
      >
        Skip to main content
      </a>
      {!isAdminRoute && <Header />}
      <main id="main-content" className="min-h-screen page-enter" key={location.pathname}>{children}</main>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <ChatWidget />}
      {!isAdminRoute && <CookieConsent />}
    </>
  );
};

// Admin routes wrapper with auth provider and sidebar layout
const AdminRoutes = () => {
  return (
    <AdminAuthProvider>
      <AdminLayout>
        <Routes>
          <Route path="login" element={<AdminLogin />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="ads" element={<AdminAds />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="ecosystem" element={<AdminEcosystem />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="bank-accounts" element={<AdminBankAccounts />} />
          <Route path="chat" element={<AdminChat />} />
          <Route path="investments" element={<AdminInvestments />} />
          <Route path="wholesale" element={<AdminWholesale />} />
          <Route path="email" element={<AdminEmailTemplates />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="farmers" element={<AdminFarmers />} />
          <Route path="cooperative-loans" element={<AdminCooperativeLoanInterests />} />
        </Routes>
      </AdminLayout>
    </AdminAuthProvider>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <CookieConsentProvider>
        <CartProvider>
          <SessionInactivityProvider>
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/ecosystem" element={<Ecosystem />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />
              <Route path="/my-orders" element={<MyOrders />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/account/security" element={<TrustedDevices />} />
              <Route path="/farmer/apply" element={<FarmerApply />} />
              <Route path="/farmer/listings" element={<FarmerListings />} />
              <Route path="/farmers-market" element={<FarmersMarket />} />
              <Route path="/investment" element={<Investment />} />
              <Route path="/wholesale" element={<Wholesale />} />
              <Route path="/track-order" element={<TrackOrder />} />
              <Route path="/legal/terms" element={<Terms />} />
              <Route path="/legal/privacy" element={<Privacy />} />
              <Route path="/legal/cookies" element={<Cookies />} />
              <Route path="/investor/dashboard" element={<Dashboard />} />
              <Route path="/account" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              {/* Legacy views kept behind feature flags for admin comparisons. */}
              <Route path="/account/legacy" element={<CustomerDashboard />} />
              <Route path="/investor/legacy" element={<InvestorDashboard />} />
              <Route path="/admin/*" element={<AdminRoutes />} />
            </Routes>
          </Layout>
          <Toaster position="top-right" />
          </SessionInactivityProvider>
        </CartProvider>
        </CookieConsentProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
